import { CdkPortalOutlet, ComponentPortal, PortalModule } from '@angular/cdk/portal';
import { NgComponentOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
  runInInjectionContext,
  signal,
  viewChild
} from '@angular/core';
import { outputToObservable, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import {
  ISidenavLayout,
  SIDENAV_LAYOUT_ADDONS,
  SidenavLayout,
  SidenavLayoutRegistry,
  SidenavRightPortalService,
  SimSidenavComponent
} from '@simOn/layout';
import { PrivilegesFacade } from '@simOn/privileges';
import { PublicLinkService } from '@simOn/public-link';
import { SpaceFacade } from '@simOn/space';
import { SidenavComponent, SimSidenavModule } from '@simOn/ui/sim-sidenav';

export const provideDefaultSideMenu = (
  sidenav: ISidenavLayout,
  spaceFacade: SpaceFacade,
  privilegesFacade: PrivilegesFacade
) => {
  sidenav.outletLeft.hasAttached() && sidenav.outletLeft.detach();
  const sidenavPortal = new ComponentPortal(SimSidenavComponent);
  const ref = sidenav.outletLeft.attach(sidenavPortal);
  const apartment = toSignal(spaceFacade.selectedApartment$);
  const _interiorVisible = toSignal(privilegesFacade.hasGeneralAccessTo$('CanManageInteriorDesignerEditor'));
  const scanType = toSignal(spaceFacade.scanType$, { initialValue: 'UNKNOWN' });
  const isPublicLink = toSignal(inject(PublicLinkService).isPublicLinkModeActive$, { initialValue: false });
  const interiorVisible = computed(() => {
    const matterportViewer = scanType();
    if (matterportViewer !== 'MATTERPORT') return false;
    // a visitor has no privileges; the side menu lists the editor for them disabled
    return isPublicLink() || _interiorVisible() || false;
  });
  const _providersDisabled = toSignal(privilegesFacade.hasGeneralAccessTo$('CanViewProviders'));
  const providersDisabled = computed(() => {
    const providersDisabled = _providersDisabled();
    return providersDisabled || false;
  });

  const _canViewDocuments = toSignal(privilegesFacade.hasDocumentsAccessTo$('CanView'));
  const canViewDocuments = computed(() => _canViewDocuments() || false);
  const stagesVisible = computed(() => !!apartment()?.stagesProjectId);

  const updateSpace = () => {
    ref.setInput('apartmentAttachment', apartment()?.attachment);
    ref.setInput('apartmentName', apartment()?.name);
    ref.setInput('interiorDesignerVisible', interiorVisible());
    ref.setInput('providersDisabled', providersDisabled());
    ref.setInput('canViewDocuments', canViewDocuments());
    ref.setInput('stagesVisible', stagesVisible());
  };

  updateSpace();

  const updateSpaceEffect = effect(() => {
    updateSpace();
  });

  outputToObservable(ref.instance.onClose)
    .pipe(takeUntilDestroyed())
    .subscribe(() => {
      sidenav.sidenavLeft.opened = false;
    });
};

@Component({
  imports: [PortalModule, SimSidenavModule, RouterModule, NgComponentOutlet],
  template: `
    <sim-sidenav-container [hasBackdrop]="hasBackdrop()" [trigger]="trigger()">
      <sim-sidenav mode="over" #sidenavLeft position="left" class="left-menu">
        <ng-template #outletLeft="cdkPortalOutlet" [cdkPortalOutlet]="selectedPortalLeft" />
      </sim-sidenav>
      <sim-sidenav mode="over" #sidenavRight position="right" class="right-menu">
        <ng-template #outletRight="cdkPortalOutlet" [cdkPortalOutlet]="selectedPortalRight" />
      </sim-sidenav>
      <sim-sidenav-content>
        <div class="content-wrapper flex">
          <div class="content-wrapper__outlet">
            <router-outlet />
          </div>
        </div>
        @for (addon of addons; track addon) {
          <ng-container *ngComponentOutlet="addon" />
        }
      </sim-sidenav-content>
    </sim-sidenav-container>
  `,
  styleUrl: './sidenav-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: SidenavLayout,
      useExisting: SidenavLayoutComponent
    },
    SidenavRightPortalService
  ]
})
export class SidenavLayoutComponent implements ISidenavLayout, OnInit, OnDestroy {
  private readonly _privilegesFacade = inject(PrivilegesFacade);
  private readonly _spaceFacade = inject(SpaceFacade);
  private readonly _renderer = inject(Renderer2);
  private readonly _injector = inject(Injector);
  private readonly _layoutRegistry = inject(SidenavLayoutRegistry);
  /** App-provided components rendered inside the layout (see `SIDENAV_LAYOUT_ADDONS`). */
  protected readonly addons = inject(SIDENAV_LAYOUT_ADDONS);
  protected selectedPortalRight: ComponentPortal<any> | undefined;
  protected selectedPortalLeft: ComponentPortal<any> | undefined;
  readonly _sidenavRight = viewChild.required<SidenavComponent>('sidenavRight');
  readonly _sidenavLeft = viewChild.required<SidenavComponent>('sidenavLeft');
  readonly _sidenavRightElementRef = viewChild.required('sidenavRight', { read: ElementRef<SidenavComponent> });
  readonly _sidenavLeftElementRef = viewChild.required('sidenavLeft', { read: ElementRef<SidenavComponent> });
  readonly routerEvent = inject(Router).events;

  readonly _outletLeft = viewChild.required<CdkPortalOutlet>('outletLeft');
  readonly _outletRight = viewChild.required<CdkPortalOutlet>('outletRight');

  readonly hasBackdrop = signal(false);

  readonly isMenuExpanded = signal<boolean>(false);
  readonly trigger = computed(() => {
    const hasBackdrop = this.hasBackdrop();
    if (this.sidenavRight.opened) {
      return hasBackdrop;
    }
    return !this.sidenavRight.opened && !hasBackdrop ? false : undefined;
  });

  ngOnInit(): void {
    this._layoutRegistry.register(this);
    runInInjectionContext(this._injector, () =>
      provideDefaultSideMenu(this, this._spaceFacade, this._privilegesFacade)
    );
  }
  ngOnDestroy(): void {
    this._layoutRegistry.unregister(this);
    this.outletLeft.hasAttached() && this.outletLeft.detach();
  }
  get sidenavRight() {
    return this._sidenavRight();
  }

  get outletLeft() {
    return this._outletLeft();
  }

  set leftPanelWidth(width: string) {
    this._renderer.setStyle(this._sidenavLeftElementRef().nativeElement, 'width', width);
  }
  get sidenavLeft(): SidenavComponent {
    return this._sidenavLeft();
  }
  get outletRight(): CdkPortalOutlet {
    return this._outletRight();
  }
  set rightPanelWidth(width: string) {
    this._renderer.setStyle(this._sidenavRightElementRef().nativeElement, 'width', width);
  }

  close() {
    this.sidenavRight.opened = false;
  }
}
